import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FaPaperPlane } from 'react-icons/fa';
import { useMutation } from '@apollo/client';
import { promiseModalDialog, toastSuccess, toastWarning } from 'motion-components';
import { useTranslation } from 'react-i18next';
import { useHandleError } from 'shared-pages/services';
import { gqlSendNotification } from '../graphql';

/** Κουμπί χρήστη: προσχέδιο → αποστολή (εσωτερικά σύνθεση + ουρά) */
export const SendNotificationButton = ({
  notificationId,
  disabled,
  recipientCount,
  onSent,
}: {
  notificationId: number;
  disabled?: boolean;
  recipientCount: number;
  onSent: () => void;
}) => {
  const { t } = useTranslation();
  const { handleGraphqlError } = useHandleError('SendNotification');
  const [send, { loading }] = useMutation(gqlSendNotification);

  const handleSend = async () => {
    if (recipientCount <= 0) {
      toastWarning(t('Προσθέστε τουλάχιστον έναν παραλήπτη πριν την αποστολή'));
      return;
    }

    const confirmed = await promiseModalDialog({
      title: t('Αποστολή ενημέρωσης'),
      text: t(
        'Θα σταλεί η ενημέρωση σε {{count}} παραλήπτες. Μετά την αποστολή δεν θα μπορείτε να αλλάξετε τους παραλήπτες.',
        { count: recipientCount },
      ),
    });
    if (!confirmed) return;

    try {
      const { data } = await send({ variables: { id: notificationId } });
      const result = data?.sendNotification;
      if (!result?.id) throw new Error(t('Αποτυχία αποστολής'));
      const extra =
        result.skippedNoEmail > 0 ? t(' (χωρίς email: {{n}})', { n: result.skippedNoEmail }) : '';
      toastSuccess(
        t('Η ενημέρωση μπήκε σε αποστολή ({{queued}} παραλήπτες)', {
          queued: result.queuedCount,
        }) + extra,
      );
      onSent();
    } catch (error) {
      handleGraphqlError('mutation-send', error);
    }
  };

  return (
    <Button
      onClick={handleSend}
      disabled={loading || disabled || recipientCount <= 0}
      className="button-animate-scale"
      type="button"
      size="sm"
      variant="success"
      title={
        disabled
          ? t('Αποθηκεύστε πρώτα τις αλλαγές')
          : recipientCount <= 0
          ? t('Προσθέστε παραλήπτες')
          : undefined
      }
    >
      {loading ? (
        <Spinner animation="border" size="sm" className="mb-1" />
      ) : (
        <span className="d-flex align-items-center">
          <FaPaperPlane className="me-2 button-icon" />
          {t('Αποστολή Ενημέρωσης')}
        </span>
      )}
    </Button>
  );
};
